import type { UploadedFile } from '@latitude/file-upload';
import { FileUpload } from '@latitude/file-upload';
import { useFileUpload } from 'hooks';
import type { FC, MouseEvent } from 'react';
import { useState } from 'react';
import type { FileUploadWithProgressProps } from 'types';

export const FileUploadWithProgress: FC<FileUploadWithProgressProps> = ({
  uploadURL,
  uploadFileName,
  onUploadComplete,
  onUploadError,
  initialFiles = [],
  onChange: propsOnChange,
  onClear: propsOnClear,
  ...restProps
}) => {
  const [files, setFiles] = useState<UploadedFile[]>(initialFiles);
  const { uploadFile } = useFileUpload();

  const handleOnComplete =
    (files: UploadedFile[]) =>
    (
      completedFile: UploadedFile,
      _response?: XMLHttpRequest['response'],
      error?: Error,
    ) => {
      const updatedFiles = files.map((file) =>
        file.name === completedFile.name ? completedFile : file,
      );
      setFiles(updatedFiles);

      if (error) {
        onUploadError?.(completedFile, error);
      } else {
        onUploadComplete?.(updatedFiles);
      }
    };

  const handleFileChange = (newFiles: UploadedFile[]) => {
    setFiles(newFiles);

    // Call the original onChange if provided
    propsOnChange?.(newFiles);

    // Start uploading each new file
    newFiles.forEach((newFile) => {
      if (newFile.file) {
        uploadFile(
          newFile,
          uploadURL,
          uploadFileName,
          handleOnComplete(newFiles),
        );
      }
    });
  };

  const handleClear = (
    event: MouseEvent<HTMLButtonElement>,
    index?: number,
  ) => {
    if (index !== undefined) {
      setFiles(files.filter((_, fileIndex) => fileIndex !== index));
    } else {
      setFiles([]);
    }

    propsOnClear?.(event, index);
  };

  return (
    <FileUpload
      {...restProps}
      externalFiles={files}
      onChange={handleFileChange}
      onClear={handleClear}
    />
  );
};
